import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App as AntdApp,
  Button,
  Card,
  Form,
  InputNumber,
  Skeleton,
  Space,
  Typography,
} from 'antd';

import { getSettings, updateSettings } from '../api/endpoints';
// SHAR-917 - numeric fields that refuse letters and symbols as they are typed.
import NumberInput from '../components/NumberInput';
import { useSingleSubmit } from '../hooks/useSingleSubmit';
// Admin regression 6 Oct — the API's upper bounds (SETTINGS_LIMITS).
import { SETTINGS_LIMITS } from '../data/limits';
import { COMMISSION_LABEL } from '../data/format';

const { Title, Text } = Typography;

/**
 * SHAR-321/322/318 — platform settings: creation lead-time window, MGAV
 * deadline and the payment processing fee.
 */
export function SettingsPage() {
  const [form] = Form.useForm();
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();

  const settings = useQuery({ queryKey: ['settings'], queryFn: getSettings });

  useEffect(() => {
    if (settings.data) form.setFieldsValue(settings.data);
  }, [settings.data, form]);

  const saveMut = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      message.success('Settings saved');
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Save failed'),
  });

  // Regression 28 Sep — one submit at a time.
  const submitGuard = useSingleSubmit();
  const submit = submitGuard.wrap(async () => {
    const v = await form.validateFields();
    await saveMut.mutateAsync(v);
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
      <div>
        <Title level={3} style={{ margin: 0 }}>Settings</Title>
        <Text type="secondary">Platform-wide booking and payment configuration.</Text>
      </div>

      <Card>
        {settings.isLoading ? (
          <Skeleton active />
        ) : (
          <Form form={form} layout="vertical">
            <Form.Item
              name="creationLeadTimeMinDays"
              label="Minimum creation lead time (days)"
              tooltip="A user can only create a booking at least this many days before departure. Can't be less than the MGAV deadline."
              rules={[
                { required: true, type: 'number', min: 1, message: 'Min 1 day' },
                { type: 'number', max: SETTINGS_LIMITS.leadTimeDays, message: `At most ${SETTINGS_LIMITS.leadTimeDays} days` },
              ]}
            >
              <NumberInput min={1} max={SETTINGS_LIMITS.leadTimeDays} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="creationLeadTimeMaxDays"
              label="Maximum creation lead time (days)"
              tooltip="Furthest ahead a booking can be created. Default 365."
              rules={[
                { required: true, type: 'number', min: 1, message: 'Min 1 day' },
                { type: 'number', max: SETTINGS_LIMITS.leadTimeDays, message: `At most ${SETTINGS_LIMITS.leadTimeDays} days` },
              ]}
            >
              <NumberInput min={1} max={SETTINGS_LIMITS.leadTimeDays} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="mgavDeadlineDays"
              label="MGAV deadline (days)"
              tooltip="How many days before departure a booking must reach MGAV. Can't be greater than the minimum creation lead time."
              rules={[
                { required: true, type: 'number', min: 1, message: 'Min 1 day' },
                { type: 'number', max: SETTINGS_LIMITS.leadTimeDays, message: `At most ${SETTINGS_LIMITS.leadTimeDays} days` },
              ]}
            >
              <NumberInput min={1} max={SETTINGS_LIMITS.leadTimeDays} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="processingFeePct"
              label={`${COMMISSION_LABEL} (%)`}
              tooltip="Percentage charged on top of the operator price and the ShareJet margin. Shown as Processing commission on the booking and charter pages."
              rules={[{ required: true, type: 'number', min: 0, max: 100, message: '0–100' }]}
            >
              <NumberInput min={0} max={100} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
            {/* SHAR-604/917 — the house margin. It lives here rather than as a
                constant in the pricing code so changing it is an admin action,
                not a deployment. SHAR-930 made it the platform's ONE margin: it
                prices ShareJet seats as well as charter quotes, where the quote
                form seeds its margin field from it and the admin can still
                override it on a particular deal. */}
            <Form.Item
              name="sharejetMarginPct"
              label="ShareJet margin (%)"
              tooltip="ShareJet's margin as a percentage of the operator price, applied to ShareJet seat pricing and used to pre-fill the margin on a new charter quote. Existing quotes and bookings keep the margin they were priced at."
              rules={[{ required: true, type: 'number', min: 0, max: 100, message: '0–100' }]}
            >
              <NumberInput min={0} max={100} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
            {/* SHAR-926 — the fixed fee, in GBP rather than percent, added to the
                final sum of every transaction (SHAR-944). It is not part of the
                seat price, so it is not a percentage of anything. */}
            <Form.Item
              name="fixedFeeGbp"
              label="Fixed fee (GBP)"
              tooltip="Added to the final sum of every transaction, on top of the price. Applies to transactions made after the change; nothing already paid is affected."
              rules={[
                { required: true, type: 'number', min: 0, message: '0 or more' },
                { type: 'number', max: SETTINGS_LIMITS.fixedFeeGbp, message: `At most ${SETTINGS_LIMITS.fixedFeeGbp} GBP` },
                {
                  validator: (_, v) =>
                    // Same tolerance as the API: 0.29 * 100 is 28.999999999999996.
                    v == null || Math.abs(v * 100 - Math.round(v * 100)) <= 1e-6
                      ? Promise.resolve()
                      : Promise.reject(new Error('At most two decimal places')),
                },
              ]}
            >
              <NumberInput min={0} max={SETTINGS_LIMITS.fixedFeeGbp} step={0.01} precision={2} addonAfter="GBP" style={{ width: '100%' }} />
            </Form.Item>
            {/* SHAR-720 — charters have their own minimum lead time. It was a
                constant of 24 hours in the API, which made the one number the
                business is most likely to want to change the one number nobody
                could. Hours, not days, because a charter is arranged to the hour
                and 24 is the value it has always had. The apps read it through
                the calendar and open on the first month that has a bookable day
                in it. */}
            <Form.Item
              name="charterLeadTimeMinHours"
              label="Charter minimum lead time (hours)"
              tooltip="How far ahead of departure a charter request can still be made. ShareJet bookings use the day-based lead times above."
              rules={[
                { required: true, type: 'number', min: 1, message: 'At least 1 hour' },
                { type: 'number', max: SETTINGS_LIMITS.charterLeadTimeHours, message: `At most ${SETTINGS_LIMITS.charterLeadTimeHours} hours` },
              ]}
            >
              <NumberInput min={1} max={SETTINGS_LIMITS.charterLeadTimeHours} step={1} style={{ width: '100%' }} />
            </Form.Item>
            <Button type="primary" loading={submitGuard.busy || saveMut.isPending} onClick={submit}>
              Save
            </Button>
          </Form>
        )}
      </Card>
    </Space>
  );
}
